// Feature: docs/features/web-based-cadwell-edf-extraction-demo.md
import React, { useState } from 'react';
import {
  Box, Container, Heading, Text, Button, Textarea, Input,
  Table, Thead, Tbody, Tr, Th, Td, Badge,
  Tabs, TabList, TabPanels, Tab, TabPanel,
  VStack, HStack, Divider, Card, CardHeader, CardBody,
  useToast, Flex
} from '@chakra-ui/react';
import { ArrowBackIcon, DownloadIcon } from '@chakra-ui/icons';
import HeaderMenus from './HeaderMenus';

// Mock initial data
const MOCK_CASES = [
  { id: '238645', recorded: '2025-11-12', duration: '8,709 seconds (~2.4 hours)', channels: 47, modalities: 'EEG, EMG, LMEP, Left LE ssep, Left UE ssep, RMEP, Right LE ssep, Right UE ssep, TOF, Trig EMG' },
  { id: '238511', recorded: '2025-11-18', duration: '13,514 seconds (~3.7 hours)', channels: 42, modalities: 'EEG, EMG, LMEP, Left LE ssep, Left TOF, Left UE ssep, RMEP, Right LE ssep, Right TOF, Right UE ssep, Trig EMG' },
  { id: '238192', recorded: '2025-11-17', duration: '3,725 seconds (~1 hour)', channels: 27, modalities: 'EEG, EMG, LMEP, Left LE ssep, Left UE ssep, Nerve Proximity, RMEP, Right LE ssep, Right UE ssep, Screw Testing, TOF, Trig EMG' },
  { id: '237896', recorded: '2025-11-19', duration: '24,386 seconds (~6.7 hours)', channels: 58, modalities: 'EEG, EMG, L- Radial SSEP, LE- TOF, LMEP, Left LE ssep, Left UE ssep, Nerve Proximity, R- Radial SSEP, RMEP, Right LE ssep, Right UE ssep, Screw Testing, Trig EMG, UE-TOF' },
  { id: '236939', recorded: '2025-11-11', duration: '16,760 seconds (~4.6 hours)', channels: 54, modalities: 'EEG, EMG, LMEP, Left PER ssep, Left PTN ssep, Left TOF, Left UE ssep, RMEP, Right PER ssep, Right PTN ssep, Right TOF, Right UE ssep, Screw Testing, TaMEP, Trig EMG' },
  { id: '236934', recorded: '2025-11-11', duration: '10,514 seconds (~2.9 hours)', channels: 44, modalities: 'EEG, EMG, LMEP, Left LAR, Left LAR (avg), Left LE ssep, Left MN ssep, Left TOF, Left UN ssep, RLN triggered EMG, RMEP, Right LAR, Right LAR (avg), Right LE ssep, Right MN ssep, Right TOF, Right UN ssep, TEMG' },
  { id: '236825', recorded: '2025-11-08', duration: '13,772 seconds (~3.8 hours)', channels: 35, modalities: 'EEG, EMG, LMEP, Left LE ssep, Left TOF, Left UE ssep, RMEP, Right LE ssep, Right TOF, Right UE ssep, Trig EMG' },
  { id: '236531', recorded: '2025-11-08', duration: '7,330 seconds (~2 hours)', channels: 58, modalities: 'EEG, EMG, L- Radial SSEP, LE- TOF, LMEP, Left LE ssep, Left UE ssep, Nerve Proximity, R- Radial SSEP, RMEP, Right LE ssep, Right UE ssep, Screw Testing, Trig EMG, UE-TOF' },
  { id: '236356', recorded: '2025-11-10', duration: '2,583 seconds (~43 minutes)', channels: 35, modalities: 'EEG, EMG, LMEP, Left LE ssep, Left UE ssep, Nerve Proximity, RMEP, Right LE ssep, Right UE ssep, Screw Testing, TOF, Trig EMG' },
  { id: '236185', recorded: '2025-11-14', duration: '11,771 seconds (~3.2 hours)', channels: 46, modalities: 'EEG, Foot TOF, Hand TOF, Left MEP, Left Tibial SSEP, Left ulnar SSEP, Right MEP, Right Tibial SSEP, Right ulnar SSEP, sp-EMG, tr-EMG' }
];

const INITIAL_JOBS = [
  {
    id: 'JOB-8492',
    status: 'Ready',
    caseCount: 1004,
    fileSize: '9.2 GB',
    createdAt: '2026-06-15 08:30 AM',
    completedAt: '2026-06-15 11:45 AM',
    cases: MOCK_CASES
  },
  {
    id: 'JOB-8493',
    status: 'Processing',
    caseCount: 250,
    fileSize: 'Calculating...',
    createdAt: '2026-06-16 09:15 AM',
    completedAt: null,
    cases: null
  },
  {
    id: 'JOB-8494',
    status: 'Pending',
    caseCount: 45,
    fileSize: 'Pending...',
    createdAt: '2026-06-16 10:00 AM',
    completedAt: null,
    cases: null
  }
];

export default function EdfExtractionDemo() {
  const [jobs, setJobs] = useState(INITIAL_JOBS);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedJob, setSelectedJob] = useState(null);
  const [caseIdsInput, setCaseIdsInput] = useState('');
  const toast = useToast();

  const handleStartExtraction = () => {
    if (!caseIdsInput.trim()) {
      toast({
        title: 'Input required',
        description: 'Please enter Case IDs or upload a CSV.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const newJob = {
      id: `JOB-${Math.floor(Math.random() * 10000) + 8500}`,
      status: 'Pending',
      caseCount: caseIdsInput.split('\n').filter(id => id.trim()).length || 1,
      fileSize: 'Pending...',
      createdAt: `2026-06-18 ${new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`,
      completedAt: null,
      cases: null
    };

    setJobs([newJob, ...jobs]);
    setCaseIdsInput('');
    setActiveTab(0); // Switch to Current Jobs tab
    
    toast({
      title: 'Extraction Started',
      description: `Job ${newJob.id} has been queued.`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Ready': return 'green';
      case 'Processing': return 'blue';
      case 'Pending': return 'yellow';
      default: return 'gray';
    }
  };

  // Job Details View
  if (selectedJob) {
    return (
      <Box minH="100vh" bg="gray.50">
        <HeaderMenus />
        <Container maxW="container.xl" py={8}>
          <Button 
            leftIcon={<ArrowBackIcon />} 
            variant="ghost" 
            mb={6} 
            onClick={() => setSelectedJob(null)}
          >
            Back to Jobs
          </Button>

          <Card mb={8} shadow="md">
            <CardHeader pb={0}>
              <Flex justify="space-between" align="center">
                <Heading size="lg">Job Details: {selectedJob.id}</Heading>
                <Button colorScheme="blue" leftIcon={<DownloadIcon />}>
                  Download ZIP ({selectedJob.fileSize})
                </Button>
              </Flex>
            </CardHeader>
            <CardBody>
              <HStack spacing={8} mb={6}>
                <Box>
                  <Text color="gray.500" fontSize="sm">Status</Text>
                  <Badge colorScheme={getStatusColor(selectedJob.status)} fontSize="md" px={2} py={1} borderRadius="md">
                    {selectedJob.status}
                  </Badge>
                </Box>
                <Box>
                  <Text color="gray.500" fontSize="sm">Cases Processed</Text>
                  <Text fontSize="lg" fontWeight="bold">{selectedJob.caseCount}</Text>
                </Box>
                <Box>
                  <Text color="gray.500" fontSize="sm">Created At</Text>
                  <Text fontSize="lg" fontWeight="bold">{selectedJob.createdAt}</Text>
                </Box>
                <Box>
                  <Text color="gray.500" fontSize="sm">Completed At</Text>
                  <Text fontSize="lg" fontWeight="bold">{selectedJob.completedAt || 'N/A'}</Text>
                </Box>
              </HStack>

              <Divider mb={6} />

              <Heading size="md" mb={4}>Extracted Cases</Heading>
              
              {selectedJob.cases ? (
                <Box overflowX="auto">
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Case ID</Th>
                        <Th>Recorded Date</Th>
                        <Th>Duration</Th>
                        <Th isNumeric>Channels</Th>
                        <Th>Modalities</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {selectedJob.cases.map((c) => (
                        <Tr key={c.id}>
                          <Td fontWeight="medium">{c.id}</Td>
                          <Td>{c.recorded}</Td>
                          <Td>{c.duration}</Td>
                          <Td isNumeric>{c.channels}</Td>
                          <Td maxW="400px" whiteSpace="normal">
                            <Text fontSize="xs" noOfLines={3}>{c.modalities}</Text>
                          </Td>
                        </Tr>
                      ))}
                      {selectedJob.caseCount > selectedJob.cases.length && (
                        <Tr>
                          <Td colSpan={5} textAlign="center" color="gray.500" py={4} fontStyle="italic">
                            ... ({selectedJob.caseCount - selectedJob.cases.length} more cases included in download)
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </Box>
              ) : (
                <Text color="gray.500">Case details will be available once the job is complete.</Text>
              )}
            </CardBody>
          </Card>
        </Container>
      </Box>
    );
  }

  // Main Dashboard View
  return (
    <Box minH="100vh" bg="gray.50">
      <HeaderMenus />
      <Container maxW="container.xl" py={8}>
        <Heading mb={2}>Cadwell EDF Extraction</Heading>
        <Text color="gray.600" mb={8}>
          Convert proprietary Cadwell exports into universally readable EDF files with rich metadata and quality metrics.
        </Text>

        <Card shadow="md">
          <CardBody>
            <Tabs index={activeTab} onChange={(index) => setActiveTab(index)} colorScheme="blue">
              <TabList mb={4}>
                <Tab fontWeight="semibold">Current Jobs</Tab>
                <Tab fontWeight="semibold">Create New Run</Tab>
              </TabList>

              <TabPanels>
                {/* Tab 1: Current Jobs */}
                <TabPanel>
                  <Flex justify="flex-start" mb={4}>
                    <Button colorScheme="blue" onClick={() => setActiveTab(1)}>
                      + Create New Job
                    </Button>
                  </Flex>
                  <Box overflowX="auto">
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Job ID</Th>
                          <Th>Status</Th>
                          <Th isNumeric>Case Count</Th>
                          <Th>Est. File Size</Th>
                          <Th>Created At</Th>
                          <Th>Action</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {jobs.map((job) => (
                          <Tr key={job.id}>
                            <Td fontWeight="medium">{job.id}</Td>
                            <Td>
                              <Badge colorScheme={getStatusColor(job.status)}>
                                {job.status}
                              </Badge>
                            </Td>
                            <Td isNumeric>{job.caseCount}</Td>
                            <Td>{job.fileSize}</Td>
                            <Td>{job.createdAt}</Td>
                            <Td>
                              <Button 
                                size="sm" 
                                colorScheme="blue" 
                                variant={job.status === 'Ready' ? 'solid' : 'outline'}
                                isDisabled={job.status !== 'Ready'}
                                onClick={() => setSelectedJob(job)}
                              >
                                View Details
                              </Button>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </TabPanel>

                {/* Tab 2: Create New Run */}
                <TabPanel>
                  <VStack align="stretch" spacing={6}>
                    <Box>
                      <Heading size="sm" mb={2}>Option 1: Paste Case IDs</Heading>
                      <Text fontSize="sm" color="gray.500" mb={2}>Enter one Case ID per line.</Text>
                      <Textarea 
                        placeholder="e.g. CASE-1001&#10;CASE-1002" 
                        rows={6}
                        value={caseIdsInput}
                        onChange={(e) => setCaseIdsInput(e.target.value)}
                      />
                    </Box>
                    
                    <HStack>
                      <Divider />
                      <Text color="gray.400" fontWeight="bold" px={4}>OR</Text>
                      <Divider />
                    </HStack>

                    <Box>
                      <Heading size="sm" mb={2}>Option 2: Upload CSV</Heading>
                      <Text fontSize="sm" color="gray.500" mb={2}>Upload a CSV with a single column containing the list of Case IDs.</Text>
                      <Input type="file" accept=".csv" p={1} />
                    </Box>

                    <Button colorScheme="blue" size="lg" alignSelf="flex-start" onClick={handleStartExtraction}>
                      Start Extraction
                    </Button>
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </CardBody>
        </Card>
      </Container>
    </Box>
  );
}
